import { useLazyQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React from 'react';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type {
  TopicPostsOrder,
  TopicPostsType,
} from '../../graphql/generated/graphql';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';
import topicPath from '../../utils/url/topicPath';
import TopicBreadcrumbs from '../TopicBreadcrumbs';
import TopicNameWithIcon from '../TopicNameWithIcon';
import TopicPostSection from './TopicPostSection';
import usePinMutations from './usePinMutations';
import { useAllTopicMap } from '../useAllTopics';

export const SUBTOPIC_POSTS_INITIAL_PAGE_SIZE = 5;

const SUBTOPIC_POSTS_SECTION_TOPIC_FRAGMENT = gql(`
  fragment SubtopicPostsSection_topic on Topic {
    id
    ...topicPath_topic
    ...TopicNameWithIcon_topic
    ancestors {
      id
    }
    posts(
      after: $postsAfterCursor
      first: $postsFirst
      orderBy: $orderBy
      type: $type
    ) {
      ...TopicPostSection_posts
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const SUBTOPIC_POSTS_QUERY = gql(/** @complexity 13 */ `
  query subtopicPosts(
    $id: ID!
    $postsAfterCursor: String
    $postsFirst: Int!
    $orderBy: TopicPostsOrder!
    $type: TopicPostsType
  ) {
    topic(id: $id) {
      ...SubtopicPostsSection_topic
    }
  }
`);

interface SubtopicPostsSectionProps
  extends Pick<
    React.ComponentProps<typeof TopicPostSection>,
    'loading' | 'controls' | 'refetch'
  > {
  orderBy: TopicPostsOrder;
  type: TopicPostsType | null;
  topic: DocumentType<typeof SUBTOPIC_POSTS_SECTION_TOPIC_FRAGMENT>;
  main: boolean;
}

const SubtopicPostsSection = ({
  orderBy,
  type,
  topic,
  ...props
}: SubtopicPostsSectionProps) => {
  const allTopicMap = useAllTopicMap();
  const initialPosts = topic.posts;
  const [loadMore, { called, data, ...args }] = useLazyQuery(
    SUBTOPIC_POSTS_QUERY,
    {
      variables: {
        id: topic.id,
        postsFirst: SUBTOPIC_POSTS_INITIAL_PAGE_SIZE * 2,
        orderBy,
        type,
      },
    },
  );

  const { pinPost, unpinPost } = usePinMutations({
    topicId: topic.id,
    type,
    orderBy,
    postsCount: _.compact(topic.posts?.edges).length,
  });

  let fetchMore = null;

  if (called) {
    fetchMore = ConnectionHelper.generateFetchMore(
      SUBTOPIC_POSTS_QUERY,
      { data, ...args },
      'topic.posts',
      { afterCursorVariableKey: 'postsAfterCursor' },
    );
  } else if (initialPosts?.pageInfo.hasNextPage) {
    fetchMore = loadMore;
  }

  const posts = data?.topic.posts || initialPosts;

  if (posts == null || posts.edges?.length === 0) return null;

  const ancestors = _.compact(
    topic.ancestors.map((ancestor) => allTopicMap[ancestor.id]),
  );

  return (
    <TopicPostSection
      {...props}
      orderBy={orderBy}
      aboveTitle={
        <TopicBreadcrumbs link topics={ancestors} size="small" showLastCaret />
      }
      fetchMore={fetchMore}
      link={topicPath(topic)}
      posts={posts}
      title={<TopicNameWithIcon topic={topic} iconSize="medium" />}
      pinPost={pinPost}
      unpinPost={unpinPost}
      topicId={topic.id}
    />
  );
};

export default SubtopicPostsSection;
